import { getHostAudience, stripPathPrefix, type Audience } from '@l2u/web-shared';

// Where each portal family lives on THIS host.
//
// The portal is ONE bundle behind THREE tenant hostnames — company, driver and
// visitor. On the legacy shared base one host answers all three, so the family
// has to travel in the path: /c/*, /d/*, /visit*. On a per-audience host the
// HOST already says which family this is, and the prefix only repeated it — so
// there the family is mounted at the root (`/login`, not `/c/login`).
//
// Only the host's own family is mounted on an audience host (App.tsx), which
// is what keeps a driver sign-in off the company host: `/d/login` there is not
// a route at all. That replaces the #98 path gate by construction — a route
// added to one family later cannot appear on another family's host.
//
// This is a ROUTING rule and not a security boundary; nothing here is the
// reason a driver cannot read a company's data. companyAuth and driverAuth
// scope every read to the identity on the bearer token regardless of host.

export type PortalFamily = 'company' | 'driver' | 'visitor';

/** The prefix each family carries where the host does not name it. */
export const LEGACY_PREFIX: Readonly<Record<PortalFamily, string>> = {
  company: '/c',
  driver: '/d',
  visitor: '/visit',
};

/**
 * The top-level segments each family's SHORT route table declares. App.tsx
 * types its page tables against these, so the two cannot drift apart without a
 * compile error; the legacy forward below reads them to tell a page from an
 * invitation token.
 */
export const COMPANY_SEGMENTS = [
  'login',
  'callback',
  'expired',
  'locked',
  'invite',
  'dashboard',
  'info',
  'questionnaire',
  'training',
  'drivers',
  'documents',
  'assets',
  'corrective-actions',
  'file-notes',
  'library',
  'preview',
] as const;

export const DRIVER_SEGMENTS = [
  'login',
  'callback',
  'expired',
  'locked',
  'invite',
  'details',
  'questionnaire',
  'training',
  'library',
  'file-notes',
  'preview',
] as const;

const SEGMENTS: Readonly<Record<'company' | 'driver', readonly string[]>> = {
  company: COMPANY_SEGMENTS,
  driver: DRIVER_SEGMENTS,
};

/**
 * The path prefix for `family` on a host serving `audience`: empty on that
 * family's own host, the legacy prefix everywhere else.
 *
 * `audience` null is the legacy shared base, local dev and the platform host —
 * every one of which serves all three families, so each keeps its prefix.
 */
export function familyBase(family: PortalFamily, audience: Audience | null): string {
  return audience === family ? '' : LEGACY_PREFIX[family];
}

/**
 * A family path on this host. `sub` is the path below the family root
 * ('/login', '/drivers/42'), or '' for the root itself — which is '/' on the
 * family's own host and the bare prefix ('/c') elsewhere.
 */
export function familyPath(
  family: PortalFamily,
  audience: Audience | null,
  sub: '' | `/${string}`,
): string {
  const path = `${familyBase(family, audience)}${sub}`;
  return path === '' ? '/' : path;
}

// The call-site forms. getHostAudience() throws until the boot gate resolves,
// which main.tsx awaits before the first render, so these are safe anywhere a
// component renders or an effect runs.
export const companyPath = (sub: '' | `/${string}`): string =>
  familyPath('company', getHostAudience(), sub);
export const driverPath = (sub: '' | `/${string}`): string =>
  familyPath('driver', getHostAudience(), sub);

/**
 * The "go somewhere real" links on this host's 404 page.
 *
 * On an audience host that is the family root, which the shell turns into the
 * home page or the sign-in form as appropriate. Where one origin serves every
 * family there is no single home, so both portals are offered. There is never
 * an admin link: /admin is served by a DIFFERENT Worker on the shared host and
 * does not exist on a portal host, so inside this bundle it is only another
 * 404.
 */
export function notFoundLinks(audience: Audience | null): { to: string; label: string }[] {
  if (audience === 'company' || audience === 'driver') return [{ to: '/', label: 'Home' }];
  if (audience === 'visitor') return [{ to: '/', label: 'Visitor sign-in' }];
  return [
    { to: familyPath('company', audience, ''), label: 'Company portal' },
    { to: familyPath('driver', audience, ''), label: 'Driver portal' },
  ];
}

/** Is this the host's own family, i.e. is it mounted at the root here? */
export function isAudienceHost(audience: Audience | null): audience is PortalFamily {
  return audience === 'company' || audience === 'driver' || audience === 'visitor';
}

/**
 * Where a PREFIXED path sent to a family's own host should go now.
 *
 * Invite emails already sent point at /c/<token>, /d/<token> and the old
 * callbacks, and an email cannot be recalled — so the old shape is forwarded,
 * never 404'd. The caller appends search and hash: the callback's session
 * fragment and any query-borne token must survive the hop.
 *
 *   company/driver: /c/login/<slug> → /login (the host carries the slug now)
 *                   /c/<one segment that is no page> → /invite/<it> (a token)
 *                   anything else → the prefix dropped
 *   visitor:        /visit, /visit/<slug>          → /
 *                   /visit/v/<id>, /visit/<slug>/v/<id> → /v/<id>
 *
 * Returns null when `pathname` is not under the family's legacy prefix.
 */
export function legacyForwardPath(family: PortalFamily, pathname: string): string | null {
  const prefix = LEGACY_PREFIX[family];
  // Segment boundary, never a bare startsWith: '/champion' is not a '/c' path.
  if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) return null;
  const rest = stripPathPrefix(prefix, pathname);
  const segments = rest.split('/').filter(Boolean);

  if (family === 'visitor') {
    const v = segments.indexOf('v');
    // Only the two shapes the visitor family ever had: [v, id] and
    // [slug, v, id]. Anything else is a hand-typed path; its landing is '/'.
    if ((v === 0 || v === 1) && segments.length === v + 2) return `/v/${segments[v + 1]}`;
    return '/';
  }

  const [first] = segments;
  if (first === 'login') return '/login';
  if (segments.length === 1 && first && !SEGMENTS[family].includes(first)) {
    return `/invite/${first}`;
  }
  return rest;
}
